import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  Avatar,
  Typography,
  Button,
  CircularProgress,
  InputAdornment,
  ClickAwayListener,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { get } from '../../utility/fetch';

const PatientSearchSelect = ({
  selectedPatientId,
  setSelectedPatientId,
  sentIds,
  requestingId,
  handleSendRequest,
  isMobile,
  theme,
  alpha,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showList, setShowList] = useState(false);
  const inputRef = useRef(null);

  const loggedInUserId = Number(sessionStorage.getItem("userId"));

  // Fetch patients when searchQuery is >= 3 characters
  useEffect(() => {
    // Derive filter from the query: starts with digit => PhoneNumber, else PatientName
    const derivedFilter = /^\s*\d/.test(searchQuery) ? 'PhoneNumber' : 'PatientName';
    // Sanitize per filter
    const q = derivedFilter === 'PhoneNumber'
      ? (searchQuery || '').replace(/\D/g, '')
      : (searchQuery || '').trim();

    if (q.length < 3) {
      setPatients([]);
      setLoading(false);
      return;
    }

    const fetchPatients = async () => {
      setLoading(true);
      try {
        const res = await get(
          `/patient/filter-list/${derivedFilter}/${encodeURIComponent(q)}/1/10`
        );
        if (res?.statusCode === 200) {
          setPatients(res.data.recordList || []);
        } else {
          setPatients([]);
        }
      } catch (err) {
        console.error('Fetch patients error', err);
        setPatients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [searchQuery]);

  // Close list when clicking outside
  const handleClickAway = () => {
    setShowList(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Single search input; filter is auto-detected */}
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            fullWidth
            ref={inputRef}
            value={searchQuery}
            onFocus={() => setShowList(true)}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={/^\s*\d/.test(searchQuery) ? 'Search by phone number...' : 'Search citizens...'}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.08),
                borderRadius: theme.shape.borderRadius * 2,
                '& fieldset': { border: 'none' },
                color: 'white',
                backdropFilter: 'blur(6px)',
              },
            }}
          />
        </Box>

        {showList && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 300,
              overflowY: 'auto',
              borderRadius: 2,
              zIndex: 10,
            }}
          >
            <List disablePadding>
              {loading ? (
                // Show skeletons while loading
                Array.from({ length: 5 }).map((_, idx) => (
                  <ListItem key={idx} sx={{ px: 2, py: 1.5 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="60%" />
                      <Skeleton width="40%" />
                    </Box>
                    <Skeleton variant="rectangular" width={60} height={30} />
                  </ListItem>
                ))
              ) : patients?.length === 0 && (
                // show "No results" only when effective query length >= 3
                (/^\s*\d/.test(searchQuery)
                  ? searchQuery.replace(/\D/g, '').length >= 3
                  : searchQuery.trim().length >= 3)
              ) ? (
                <ListItem>
                  <Typography variant="body2" color="text.secondary">
                    No results found
                  </Typography>
                </ListItem>
              ) : (
                patients.map((option) => (
                  <ListItem
                    key={option.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': { backgroundColor: '#f9f9f9' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={option.pictureUrl} alt={option.firstName} />
                      <Box>
                        <Typography fontWeight={600} fontSize="0.95rem">
                          {option.firstName} {option.lastName}
                        </Typography>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      {sentIds.includes(option.id) || option.followList?.includes(loggedInUserId) ? (
                        <Button variant="outlined" disabled size="small">
                          Sent
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSendRequest(option.id, false)}
                          sx={{ textTransform: 'none', px: 2 }}
                        >
                          {requestingId === option.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            'Follow'
                          )}
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};



export default PatientSearchSelect;
